import { NavLink } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { can, ROLE_LABELS } from '@/types';
import { NAV_ITEMS, SECTION_LABELS, type NavItem } from '@/routes/nav';
import { BrandMark } from '@/components/ui/BrandMark';
import { cn, initials } from '@/lib/utils';

export function Sidebar() {
  const { user, signOutUser } = useAuth();
  if (!user) return null;

  // Filter nav items by role
  const visible = NAV_ITEMS.filter((i) => can(user.role, i.module, 'read'));
  const bySection = visible.reduce<Record<string, NavItem[]>>((acc, item) => {
    (acc[item.section] ||= []).push(item);
    return acc;
  }, {});

  return (
    <aside className="fixed inset-y-0 left-0 z-30 w-64 flex flex-col bg-bg-subtle border-r border-border">
      {/* Brand */}
      <div className="h-16 px-5 flex items-center border-b border-border shrink-0">
        <BrandMark compact />
        <div className="ml-3 leading-tight">
          <div className="font-display text-sm text-text">Talbert</div>
          <div className="kicker text-[0.6rem]">Admin</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        {(Object.keys(bySection) as Array<keyof typeof SECTION_LABELS>).map((section) => (
          <div key={section}>
            <div className="kicker px-3 mb-2">{SECTION_LABELS[section]}</div>
            <ul className="space-y-0.5">
              {bySection[section].map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/'}
                      className={({ isActive }) =>
                        cn(
                          'group flex items-center gap-3 px-3 h-9 rounded-sm',
                          'text-sm transition-colors duration-150',
                          isActive
                            ? 'bg-bg-hover text-text border-l-2 border-brass-400 pl-[10px]'
                            : 'text-text-muted hover:text-text hover:bg-bg-hover/50',
                        )
                      }
                    >
                      <Icon size={16} className="shrink-0" />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* User */}
      <div className="border-t border-border p-3 shrink-0">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 shrink-0 rounded-sm bg-walnut-700 flex items-center justify-center font-display text-sm text-brass-200">
            {initials(user.displayName || user.email)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-text truncate">
              {user.displayName || user.email.split('@')[0]}
            </div>
            <div className="kicker text-[0.6rem] text-brass-300">
              {ROLE_LABELS[user.role]}
            </div>
          </div>
          <button
            onClick={() => void signOutUser()}
            aria-label="Sign out"
            className="p-2 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm transition-colors"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </aside>
  );
}
